import type { FunctionComponent, ReactNode } from 'react'
import { useState, useRef, useEffect, useMemo } from 'react'
import {
  getAiTeammateWidgetService,
  IWidgetComponentApi,
  type AiTeammateWidgetData,
} from '@playbooks/action-cards-utils'
import { AiTeammateRealtimeModel, AiTeammateScope } from '@playbooks/core/src/interfaces/ActionCard'
import { getTeammateTargetSections } from './aiTeammate/getTeammateTargetSections'
import { resolveTeammateUpdate, type CompactTeammateUpdate } from './aiTeammate/resolveTeammateUpdate'
import { shortenTeammateIds } from './aiTeammate/shortenTeammateIds'

import { type WidgetProps } from './widgetTypes'
import { CustomButton } from '../../components/ui/CustomButton'
import { traverseNodes } from '../../utils/actionCardOperations'
import { Mic, MicOff, X, CheckCircle2, ChevronDown, Clipboard, ClipboardCheck } from 'lucide-react'
import { useTranslation } from '../../contexts/localization/Localization'
import { applyAiResponse } from '../../services/ai/applyAiResponse'
import type { GeneratedPlayResponse } from '@playbooks/core/src/services/BackendClient'
import { usePlaybooksBackendClient } from '../../hooks/usePlaybooksBackendClient'
import useComponentsApiRegistry from '../../hooks/useComponentsApiRegistry'
import { useAppContext } from '../../contexts/AppContext'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import { TeammateAudioSourceController } from './aiTeammate/teammateAudioSourceController'
import {
  TeammateRealtimeController,
  type TeammateRealtimeErrorCode,
  type TeammateRealtimeEvent,
  type TeammateRealtimeStatus,
} from './aiTeammate/teammateRealtimeController'
import { registerTeammateConsoleControls, type TeammateConsoleControls } from './aiTeammate/teammateConsoleApi'
import useMountEffect from '../../hooks/useMountEffect'
import Disclaimer from '../../components/ui/Disclaimer'

// Global Window Types for Developer Debugging
declare global {
  interface Window {
    constants?: {
      aiTeammateWidget?: {
        showLog?: boolean
        /** Start listening in the mounted AI Teammate widget with the given ID */
        start?: (widgetId: number) => Promise<void>
        /** Stop listening in the mounted AI Teammate widget with the given ID */
        stop?: (widgetId: number) => Promise<void>
      }
    }
  }
}

interface AiTeammateWidgetProps extends WidgetProps {
  widget: AiTeammateWidgetData
}

interface CollapsibleHeaderProps {
  title: ReactNode
  isOpen: boolean
  onToggle: () => void
}

/** Minimum confidence required to apply the answer to the Action Card */
const minAnswerConfidence = 0.2

/** Safety timeout duration in milliseconds (2 hours) */
const SAFETY_TIMEOUT_MS = 2 * 60 * 60 * 1000

/** Debounce duration for persisting transcript changes */
const TRANSCRIPT_SAVE_DEBOUNCE_MS = 10000

const realtimeModelOptions = Object.values(AiTeammateRealtimeModel)

/**
 * AI Teammate Widget - Real-time speech-to-action-card processing
 * Listens to user's microphone and fills out the Action Card based on speech using OpenAI Realtime API.
 * Emphasizes low latency and high responsiveness for real-time call assistance.
 * @story User Story 13796: AI Teammate - reduce latency
 */
const AiTeammateWidget: FunctionComponent<AiTeammateWidgetProps> = ({ widget, context }) => {
  const {
    data: state,
    update: updateState,
    getActualState,
    registerActiveAiTeammateSession,
    unregisterActiveAiTeammateSession,
    rootRef,
  } = useAppContext()

  const playbooksBackendClient = usePlaybooksBackendClient()

  // State
  const [isListening, setIsListening] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [captureAppConnected, setCaptureAppConnected] = useState(false)
  const [generatedResponses, setGeneratedResponses] = useState<GeneratedPlayResponse[]>([])
  const [questionLabels, setQuestionLabels] = useState<Record<number, string>>({})
  const [answerLabels, setAnswerLabels] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [showLog, setShowLog] = useState(false)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [transcript, setTranscript] = useState(widget.value?.transcript?.trim() ?? '')
  const { copyToClipboard: copyTranscriptToClipboard, isCopied: isTranscriptCopied } = useCopyToClipboard()

  // Refs for stateful side-effect controllers
  const audioSourceControllerRef = useRef<TeammateAudioSourceController | null>(null)
  const realtimeControllerRef = useRef<TeammateRealtimeController | null>(null)
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const transcriptSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const realtimeEventHandlerRef = useRef<(event: TeammateRealtimeEvent) => void>(() => undefined)
  const consoleControlsRef = useRef<TeammateConsoleControls | null>(null)
  /** In-flight start, used to keep concurrent start calls from starting a second session */
  const startPromiseRef = useRef<Promise<void> | null>(null)

  // Keep a ref to the latest widget data for use in async callbacks without stale closures
  const widgetRef = useRef(widget)
  // Disable lint warning: we are only updating the ref, not actually using it for rendering
  // eslint-disable-next-line react-hooks/refs
  widgetRef.current = widget

  // Translation
  const { text: t } = useTranslation()

  // Determines if the extraction log is visible
  const isLogVisible = widget.showLog || !!window?.constants?.aiTeammateWidget?.showLog
  const shouldRecordTranscript = widget.recordTranscript ?? false
  const shouldFillAutomatically = widget.fillAutomatically ?? true
  const selectedScope = widget.scope ?? AiTeammateScope.CurrentChapter

  // Sections the teammate should fill out, based on the configured scope.
  // Long Action Card IDs are replaced with short ones to save tokens in AI instructions and tool calls.
  // The short IDs stay stable during a teammate session, as the Action Card structure doesn't change during a play.
  const { sections: actionCardSections, actionCardIdMap } = useMemo(() => {
    return shortenTeammateIds(getTeammateTargetSections(state, context?.section.id, selectedScope))
  }, [state, context?.section.id, selectedScope])

  /**
   * Persist any pending transcript change immediately.
   */
  function flushTranscriptSave() {
    // Clear the transcript save timer
    const hasPendingSave = !!transcriptSaveTimerRef.current
    clearTranscriptSaveTimer()

    if (hasPendingSave) {
      // Persist the Action Card state
      updateState()
    }
  }

  /**
   * Clear the transcript save timer without persisting changes
   */
  function clearTranscriptSaveTimer() {
    if (transcriptSaveTimerRef.current) {
      clearTimeout(transcriptSaveTimerRef.current)
      transcriptSaveTimerRef.current = null
    }
  }

  /**
   * Get or create the audio source controller instance,
   * which owns microphone and capture-app audio resources.
   */
  function getAudioSourceController() {
    if (!audioSourceControllerRef.current) {
      audioSourceControllerRef.current = new TeammateAudioSourceController()
    }
    return audioSourceControllerRef.current
  }

  /**
   * Get or create the OpenAI Realtime controller instance,
   * which manages the Realtime API connection and events.
   */
  function getRealtimeController() {
    if (!realtimeControllerRef.current) {
      realtimeControllerRef.current = new TeammateRealtimeController({
        backendClient: playbooksBackendClient,
        recordTranscript: shouldRecordTranscript,
        fillAutomatically: shouldFillAutomatically,
        model: getRealtimeModel(widget.model),
        getErrorMessage,
        onEvent: event => realtimeEventHandlerRef.current(event),
        onStatusChange: handleRealtimeStatusChange,
        onError: message => setError(message),
      })
      realtimeControllerRef.current.setActionCardSections(actionCardSections)
    }
    return realtimeControllerRef.current
  }

  /**
   * Handle status changes from the Realtime controller to update UI state accordingly
   */
  function handleRealtimeStatusChange(status: TeammateRealtimeStatus) {
    if (status === 'connecting') {
      setIsConnecting(true)
      setIsListening(false)
    } else if (status === 'listening') {
      setIsListening(true)
      setIsConnecting(false)
      registerActiveAiTeammateSession({
        widgetId: widget.id,
        stop: stopListening,
      })
    } else {
      // Status is 'stopped'
      audioSourceControllerRef.current?.stop()
      clearSafetyTimer()
      setIsListening(false)
      setIsConnecting(false)
      unregisterActiveAiTeammateSession(widget.id)
    }
  }

  /**
   * Get the error message for a given Realtime error code
   */
  function getErrorMessage(code: TeammateRealtimeErrorCode) {
    if (code === 'audioUnavailable') return t('aiTeammateWidget_audioUnavailable')
    return t('aiTeammateWidget_connectionLost')
  }

  /**
   * Start safety timer to automatically stop listening after timeout
   */
  function startSafetyTimer() {
    clearSafetyTimer()
    safetyTimerRef.current = setTimeout(() => {
      console.log('Safety timeout reached, stopping listening')
      stopListening()
      setError(t('aiTeammateWidget_timeoutReached'))
    }, SAFETY_TIMEOUT_MS)
  }

  /**
   * Clear the safety timer
   */
  function clearSafetyTimer() {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current)
      safetyTimerRef.current = null
    }
  }

  // Initialize browser environment tracking flags for developer console overrides
  useMountEffect(() => {
    if (!window) return

    window.constants = window.constants || {}
    window.constants.aiTeammateWidget = window.constants.aiTeammateWidget || {}

    console.log(
      `💡 [AI Teammate] Console API: 'window.constants.aiTeammateWidget' → 'showLog = true' reveals extracted log, 'start(${widget.id})' / 'stop(${widget.id})' control the widget.`,
    )

    // Expose start/stop of this widget in the browser console (calling the latest controls, see consoleControlsRef below)
    return registerTeammateConsoleControls(widget.id, {
      start: async () => consoleControlsRef.current?.start(),
      stop: async () => consoleControlsRef.current?.stop(),
    })
  })

  // Probe capture app WebSocket on mount
  // (check if audio capture app is running and available for system audio streaming)
  useMountEffect(() => {
    const audioSourceController = getAudioSourceController()
    audioSourceController.probeCaptureApp(setCaptureAppConnected)
  })

  // Cleanup on unmount
  useMountEffect(() => {
    return () => {
      flushTranscriptSave()
      void realtimeControllerRef.current?.dispose()
      realtimeControllerRef.current = null
      audioSourceControllerRef.current?.dispose()
      audioSourceControllerRef.current = null
      clearSafetyTimer()
      unregisterActiveAiTeammateSession(widget.id)
    }
  })

  /**
   * Update the Realtime controller with the latest Action Card sections.
   * This effect only updates a ref, with no react state updates,
   * so was considered a safe exception from the no-effects rule.
   */
  useEffect(() => {
    realtimeControllerRef.current?.setActionCardSections(actionCardSections)
  }, [actionCardSections])

  /**
   * Start listening, reusing the start already in progress, if any.
   * Concurrent calls (a double click, or the console API) cannot be caught by the `isConnecting` state,
   * which is only updated on the next render, nor by the Realtime controller, which becomes active
   * only once the audio stream is acquired.
   */
  async function startListening() {
    startPromiseRef.current ??= startListeningInternal().finally(() => {
      startPromiseRef.current = null
    })
    await startPromiseRef.current
  }

  /**
   * Start listening — uses capture app WebSocket if available, otherwise falls back to mic
   */
  async function startListeningInternal() {
    try {
      setIsConnecting(true)
      setError(null)

      const savedTranscript = shouldRecordTranscript ? (widget.value?.transcript?.trim() ?? '') : ''
      setTranscript(savedTranscript)

      const { stream } = await getAudioSourceController().startPreferredSource()
      await getRealtimeController().start(stream)
      startSafetyTimer()
    } catch (err) {
      console.error('Error starting listening:', err)
      setError(err instanceof Error ? err.message : 'Failed to start listening')
      setIsConnecting(false)
      setIsListening(false)
      await realtimeControllerRef.current?.stop()
      audioSourceControllerRef.current?.stop()
    }
  }

  /**
   * Start listening unless already listening or connecting
   */
  async function startListeningIfIdle() {
    if (!(isListening || isConnecting || realtimeControllerRef.current?.isActive())) {
      await startListening()
    }
  }

  /**
   * Stop listening and cleanup resources
   */
  async function stopListening() {
    flushTranscriptSave()
    await realtimeControllerRef.current?.stop()
    audioSourceControllerRef.current?.stop()
    clearSafetyTimer()
    setIsListening(false)
    setIsConnecting(false)
  }

  /**
   * Clear the responses log
   */
  function clearLog() {
    setGeneratedResponses([])
  }

  /**
   * Schedule a delayed Action Card update in order to persist the actual transcript
   */
  function scheduleTranscriptSave() {
    if (!shouldRecordTranscript) return

    // Do not schedule if already scheduled
    if (transcriptSaveTimerRef.current) return

    // Schedule Action Card update
    transcriptSaveTimerRef.current = setTimeout(() => {
      transcriptSaveTimerRef.current = null
      // Persist the Action Card state
      updateState()
    }, TRANSCRIPT_SAVE_DEBOUNCE_MS)
  }

  /**
   * Handle events from OpenAI Realtime API
   */
  function handleRealtimeEvent(event: TeammateRealtimeEvent) {
    // Handle function call completion
    if (shouldFillAutomatically && event.type === 'response.done') {
      const outputs = event.response?.output || []

      for (const output of outputs) {
        if (
          output.type === 'function_call' &&
          output.name === 'update_action_card' &&
          typeof output.arguments === 'string'
        ) {
          try {
            const args = JSON.parse(output.arguments) as { upd: CompactTeammateUpdate[] }
            console.log('Function call arguments:', args)
            // Resolve short IDs used by AI to the real Action Card IDs
            processActionCardUpdates(
              args.upd.map(update => resolveTeammateUpdate(update, actionCardIdMap)).filter(update => !!update),
            )
          } catch (err) {
            console.error('Error parsing function call arguments:', err)
          }
        }
      }
    }

    if (
      shouldRecordTranscript &&
      event.type === 'conversation.item.input_audio_transcription.completed' &&
      event.transcript
    ) {
      // Store transcript in state and schedule an Action Card update
      setTranscript(currentTranscript => {
        // Append new transcript to existing transcript with a separator
        const nextTranscript = appendTranscript(currentTranscript, event.transcript, '\n')
        // Put the transcript into the widget value without triggering a state update yet
        // (to avoid excessive updates while the user is speaking)
        getAiTeammateWidgetService(widgetRef.current).setValue({ transcript: nextTranscript })
        // Schedule an Action Card update to persist the new transcript after a debounce period
        scheduleTranscriptSave()
        return nextTranscript
      })
    }

    // Handle errors
    if (event.type === 'error') {
      if (canErrorBeIgnored(event?.error)) {
        console.error('Ignoring benign Realtime API error', { event })
        return
      }
      console.error('Realtime API error:', event.error)
      setError(event.error?.message || 'An error occurred')
    }
  }

  /**
   * Process updates from the AI and apply them to the Action Card
   */
  function processActionCardUpdates(updates: GeneratedPlayResponse[]) {
    if (!updates || !Array.isArray(updates)) return

    const newGeneratedResponses: GeneratedPlayResponse[] = []

    // Update question labels for display in the log
    setQuestionLabels(prev => ({ ...prev, ...getQuestionLabels(updates) }))

    for (const update of updates) {
      // Add to generated responses log
      newGeneratedResponses.push({
        ...update,
        // These log records are mostly for debugging, so purity doesn't matter
        // eslint-disable-next-line react-hooks/purity
        timestamp: Date.now(),
      })

      // Update answer labels for display
      setAnswerLabels(prev => ({ ...prev, ...getAnswerLabels(update) }))

      if (update.confidence >= minAnswerConfidence) {
        // Apply to the action card
        applyUpdateToActionCard(update)
        rootRef.current?.dispatchEvent(new CustomEvent('aiTeammateWidget:responseApplied', { detail: update }))
      }
    }

    // Last update element is used to scroll into view if not visible
    const lastUpdateElement = updates.at(-1)
    const element = rootRef.current?.querySelector(`#section-element-${lastUpdateElement?.questionId}`)

    // Check if the lastUpdateElement, element are and confidence are valid
    if (element && lastUpdateElement && lastUpdateElement?.confidence >= minAnswerConfidence) {
      const rootHeight = rootRef.current?.getBoundingClientRect().height ?? 0
      const elementTop = element.getBoundingClientRect().top
      const isElementInScrollArea = elementTop >= rootHeight * 0.8

      if (isElementInScrollArea) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    // Update the log display
    if (newGeneratedResponses.length > 0) {
      setGeneratedResponses(prev => [...newGeneratedResponses, ...prev].slice(0, 50)) // Keep last 50 updates

      // Update the Action Card state
      updateState()
    }
  }

  /**
   * Apply a single update to the Action Card
   */
  function applyUpdateToActionCard(update: GeneratedPlayResponse) {
    const latestState = getActualState().data
    traverseNodes(latestState, 'id', (node, key) => {
      if (node[key] === update.questionId) {
        applyAiResponse(update, node)
      }
    })
  }

  /**
   * Get human-readable labels for the provided question/assessment IDs
   */
  function getQuestionLabels(updates: GeneratedPlayResponse[]): Record<number, string> {
    const questionLabels: Record<number, string> = {}
    traverseNodes(state, 'id', (node, key) => {
      if (updates.some(update => update.questionId === node[key])) {
        questionLabels[node[key]] = node.text || node.question
      }
    })
    return questionLabels
  }

  /**
   * Get human-readable labels for the provided answer IDs (for dropdown/assessment questions)
   */
  function getAnswerLabels(update: GeneratedPlayResponse): Record<number, string> {
    const answerLabels: Record<number, string> = {}
    if (!update.answerIds?.length) return answerLabels
    traverseNodes(state, 'id', (node, key) => {
      if (update.answerIds?.some(id => node[key] === id)) {
        answerLabels[node[key]] = node.name || node.answer
      }
    })
    return answerLabels
  }

  /**
   * Determine if a Realtime API error is benign and can be ignored
   */
  function canErrorBeIgnored(error?: { type?: string; message?: string }): boolean {
    const message = error?.message
    return (
      // We ignore "Conversation already has an active response" error as it doesn't seem to impact the functionality
      error?.type === 'invalid_request_error' ||
      (typeof message === 'string' && message.includes('already has an active response'))
    )
  }

  /**
   * Get the normalized realtime model preset name configured in the widget
   */
  function getRealtimeModel(model: unknown): AiTeammateRealtimeModel | undefined {
    // Due to the specifics of the composer, legacy Teammate widgets might have model filled
    // accidentally with a non-realtime or otherwise invalid value. Ignore such values.
    return typeof model === 'string' && realtimeModelOptions.includes(model as AiTeammateRealtimeModel)
      ? (model as AiTeammateRealtimeModel)
      : undefined
  }

  useComponentsApiRegistry(widget.id, () => {
    return {
      activate: startListeningIfIdle,
    } as IWidgetComponentApi
  })

  // Keep the controller callback current without recreating the controller.
  // Why lint suppression necessary: this ref is read by external Realtime channel callbacks,
  // which can fire between render commit and effects. Updating it during render avoids a brief
  // stale-handler window; the write does not affect rendering.
  // eslint-disable-next-line react-hooks/refs
  realtimeEventHandlerRef.current = handleRealtimeEvent

  // Keep the console controls current (same reasoning as above: read by external console calls, does not affect rendering)
  // eslint-disable-next-line react-hooks/refs
  consoleControlsRef.current = { start: startListeningIfIdle, stop: stopListening }

  return (
    <div className="space-y-4">
      <Disclaimer
        disclaimer={{
          title: t('aiDisclaimerTitle'),
          content: t('aiDisclaimerContent'),
        }}
      />

      {/* Control Button */}
      <div className="flex items-center gap-3">
        <CustomButton
          data-testid="ai-teammate-toggle-button"
          size={'lg'}
          variant={isListening ? 'destructive' : 'default'}
          isLoading={isConnecting}
          onClick={() => {
            if (isListening || realtimeControllerRef.current?.isActive()) {
              stopListening()
            } else {
              startListening()
            }
          }}
        >
          <div className="flex gap-1 items-center">
            {isConnecting ? (
              <>{t('aiTeammateWidget_starting')}</>
            ) : isListening ? (
              <>
                <MicOff className="h-5 w-5" />
                {t('aiTeammateWidget_stopListening')}
              </>
            ) : (
              <>
                <Mic className="h-5 w-5" />
                {widget.buttonLabel || t('aiTeammateWidget_defaultButton')}
              </>
            )}
          </div>
        </CustomButton>

        {captureAppConnected && !isListening && (
          <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t('aiTeammateWidget_appConnected')}
          </div>
        )}

        {isListening && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse" />
            {t('aiTeammateWidget_listening')}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-md">
          {error}
        </div>
      )}

      {/* Live Log */}
      {isLogVisible && generatedResponses.length > 0 && (
        <div className="mt-4">
          <CollapsibleHeader
            title={
              <>
                {t('aiTeammateWidget_extractedInformation')} {generatedResponses.length}
              </>
            }
            isOpen={showLog}
            onToggle={() => setShowLog(prev => !prev)}
          />

          {/* Conditionally show log entries */}
          {showLog && (
            <div className="relative bg-gray-50 dark:bg-gray-900 rounded-md p-3 pr-9 max-h-50 overflow-y-auto space-y-1">
              <button
                type="button"
                onClick={clearLog}
                className="absolute right-2 top-2 rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
                title={t('aiTeammateWidget_clearLog')}
                aria-label={t('aiTeammateWidget_clearLog')}
              >
                <X className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
              </button>
              {generatedResponses.map((update, index) => (
                <div key={`${update.questionId}-${update.timestamp}-${index}`} className="text-sm font-mono">
                  <span className="text-gray-500">· </span>
                  <span className="font-medium">
                    {questionLabels[update.questionId] || `${t('aiTeammateWidget_question')} #${update.questionId}`}:
                  </span>{' '}
                  <span className="text-gray-700 dark:text-gray-300">
                    {update.answer ||
                      update.answerIds
                        ?.map(id => answerLabels[id] || `${t('aiTeammateWidget_option')} #${id}`)
                        .join(', ') || <em>{t('aiTeammateWidget_noAnswer')}</em>}
                  </span>
                  {update.confidence < 0.8 && (
                    <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">
                      {t('aiTeammateWidget_uncertain')}
                    </span>
                  )}
                  {update.confidence < minAnswerConfidence && (
                    <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                      ({(update.confidence * 100).toFixed(0)}% {t('aiTeammateWidget_uncertain_ignored')})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transcript */}
      {shouldRecordTranscript && (
        <div className="mt-4">
          <CollapsibleHeader
            title={t('aiTeammateWidget_transcript')}
            isOpen={isTranscriptOpen}
            onToggle={() => setIsTranscriptOpen(prev => !prev)}
          />

          {isTranscriptOpen && (
            <div className="relative bg-gray-50 dark:bg-gray-900 rounded-md p-3 pr-9 max-h-50 overflow-y-auto">
              {transcript && (
                <button
                  type="button"
                  onClick={() => copyTranscriptToClipboard(transcript)}
                  className="absolute right-2 top-2 rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
                  title={t('copyToClipboard')}
                  aria-label={t('copyToClipboard')}
                >
                  {isTranscriptCopied ? (
                    <ClipboardCheck className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  ) : (
                    <Clipboard className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                  )}
                </button>
              )}
              <pre className="text-sm font-mono whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {transcript || <em>{t('aiTeammateWidget_noTranscript')}</em>}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function appendTranscript(
  existingTranscript: string | undefined,
  sessionTranscript: string | undefined,
  separator = '\n\n',
) {
  return [existingTranscript, sessionTranscript]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(separator)
}

function CollapsibleHeader({ title, isOpen, onToggle }: CollapsibleHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between mb-2 text-left"
      aria-expanded={isOpen}
    >
      <h4 className="text-sm font-medium">{title}</h4>
      <ChevronDown
        className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  )
}

export default AiTeammateWidget
